import {
  getAuthorizedComedianIdsServer,
  getAuthorizedVenuePagesIdsServer,
} from "../../legacy/isDashboardAuthorizedUser";

import { Database } from "../../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import type { WaitlistRow } from "../../legacy/waitlist-api";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import { isSuperAdmin } from "../../legacy/isSuperAdmin";
import { logException } from "@punchup/sentry";
import { notFound } from "next/navigation";

export interface GetManagerWaitlistParams {
  supabase: SupabaseClient<Database>;
  ownerId: string;
  isVenue: boolean;
}

export interface ManagerWaitlistResult {
  waitlistEntries: WaitlistRow[];
  uniqueShowNames: string[];
}

/**
 * Server-side fetcher for the manager waitlist dashboard view (comedian or
 * venue). Performs the same per-owner authorization that the
 * /api/v2/manager/waitlist route does -- super admin, comedian admin, or
 * venue_page admin whose page covers `ownerId` -- then reads the waitlist
 * via the service-role client. RLS on public.waitlist is locked to
 * service_role only, so the cookie client cannot read directly.
 *
 * Calls notFound() on unauthorized access (instead of 403) so we don't
 * leak whether a given comedian/venue has waitlist entries. Throws on
 * actual DB errors during the read.
 *
 * Shape matches the legacy getWaitlistData so SSR pages can swap without
 * UI changes.
 */
export async function getManagerWaitlist({
  supabase,
  ownerId,
  isVenue,
}: GetManagerWaitlistParams): Promise<ManagerWaitlistResult> {
  const [authorizedComedianIds, authorizedVenuePageIds, superAdmin] =
    await Promise.all([
      getAuthorizedComedianIdsServer(supabase),
      getAuthorizedVenuePagesIdsServer(supabase),
      isSuperAdmin(supabase).catch((error) => {
        logException(error, {
          extra: { ownerId, context: "getManagerWaitlist: isSuperAdmin" },
        });
        return false;
      }),
    ]);

  let authorized = superAdmin;
  if (!authorized) {
    if (isVenue) {
      if (authorizedVenuePageIds.length > 0) {
        const { data: pages, error } = await supabase
          .from("venue_pages")
          .select("location_ids")
          .in("id", authorizedVenuePageIds);
        if (error) {
          logException(error, {
            extra: {
              ownerId,
              context: "getManagerWaitlist: venue_pages lookup",
            },
          });
        } else {
          authorized = (pages ?? []).some((p) =>
            (p.location_ids ?? []).includes(ownerId)
          );
        }
      }
    } else {
      authorized = authorizedComedianIds.includes(ownerId);
    }
  }

  if (!authorized) {
    notFound();
  }

  const admin = createSupabaseAdminClient();
  const [
    { data: entries, error: entriesError },
    { data: showNames, error: showNamesError },
  ] = await Promise.all([
    admin
      .from("waitlist")
      .select(
        `
        *,
        comedian_shows!waitlist_show_id_fkey!inner (
          id,
          comedian_id,
          datetime,
          venues (
            name,
            city,
            state
          )
        )
      `
      )
      // Service-role read: RLS does not hide soft-deleted shows here, so a
      // deleted show's waitlist is dropped explicitly (as the old cascade did).
      .is("comedian_shows.deleted_at", null)
      .eq("owner_id", ownerId)
      .eq("is_venue", isVenue)
      .order("created_at", { ascending: false }),
    admin
      .from("waitlist")
      .select("show_name")
      .eq("owner_id", ownerId)
      .eq("is_venue", isVenue)
      .not("show_name", "is", null),
  ]);

  if (entriesError) {
    throw new Error(
      `Failed to fetch waitlist entries: ${entriesError.message}`
    );
  }
  if (showNamesError) {
    throw new Error(
      `Failed to fetch waitlist show names: ${showNamesError.message}`
    );
  }

  const uniqueShowNames = Array.from(
    new Set(
      (showNames ?? [])
        .map((row: { show_name: string | null }) => row.show_name)
        .filter((name): name is string => !!name)
    )
  ).sort();

  return {
    waitlistEntries: (entries ?? []) as unknown as WaitlistRow[],
    uniqueShowNames,
  };
}
